import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { LayoutDashboard, Users, GraduationCap, CheckSquare, FolderKanban, Calendar, LogOut, Menu, X, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/coach", label: "Dashboard", icon: LayoutDashboard, end: true, tid: "coach-nav-dashboard-link" },
  { to: "/coach/apprenants", label: "Apprenants", icon: Users, tid: "coach-nav-apprenants-link" },
  { to: "/coach/seances", label: "Séances", icon: GraduationCap, tid: "coach-nav-seances-link" },
  { to: "/coach/missions", label: "Missions", icon: CheckSquare, tid: "coach-nav-missions-link" },
  { to: "/coach/ressources", label: "Ressources", icon: FolderKanban, tid: "coach-nav-ressources-link" },
  { to: "/coach/messagerie", label: "Messagerie", icon: MessageCircle, tid: "coach-nav-messagerie-link", badge: true },
  { to: "/coach/calendrier", label: "Calendrier", icon: Calendar, tid: "coach-nav-calendrier-link" },
];

export default function CoachLayout() {
  const { coach, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!loading && !coach) navigate("/", { replace: true });
  }, [coach, loading, navigate]);

  useEffect(() => {
    if (!coach) return;
    const poll = () => api.get("/coach/messages/unread_count").then(({ data }) => setUnread(data.count)).catch(() => {});
    poll();
    const t = setInterval(poll, 20000);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [coach]);

  if (loading || !coach) {
    return <div className="min-h-screen flex items-center justify-center text-[#5C6561]">Chargement…</div>;
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-[#E6E1DA] bg-white sticky top-0 z-30">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#D95D39] text-white flex items-center justify-center font-bold text-sm">D.</div>
          <span className="font-serif-accent">Décomplexé<span className="text-[#D95D39]">.e</span></span>
        </div>
        <button data-testid="coach-mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <div className="flex">
        <aside className={`${open ? "block" : "hidden"} lg:block fixed lg:static inset-x-0 top-[57px] lg:top-0 z-20 bg-white border-r border-[#E6E1DA] lg:min-h-screen lg:w-64 w-full`}>
          <div className="hidden lg:flex items-center gap-2 px-6 py-6 border-b border-[#E6E1DA]">
            <div className="w-9 h-9 rounded-xl bg-[#D95D39] text-white flex items-center justify-center font-bold">D.</div>
            <span className="font-serif-accent text-lg">Décomplexé<span className="text-[#D95D39]">.e</span></span>
          </div>
          <nav className="p-3 space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={l.tid}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${
                    isActive
                      ? "bg-[#FDF0EC] text-[#D95D39]"
                      : "text-[#1F2421] hover:bg-[#F4EFEA]"
                  }`
                }
              >
                <l.icon className="w-4 h-4" />
                {l.label}
                {l.badge && unread > 0 && (
                  <span className="ml-auto min-w-5 h-5 px-1.5 rounded-full bg-[#D95D39] text-white text-[11px] font-semibold flex items-center justify-center" data-testid="coach-unread-badge">{unread}</span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-[#E6E1DA] mt-2">
            <div className="px-3 py-2 text-xs text-[#5C6561] truncate">{coach.email}</div>
            <Button
              data-testid="coach-logout-button"
              variant="outline"
              className="w-full justify-start bg-transparent border-[#E6E1DA]"
              onClick={async () => { await logout(); navigate("/"); }}
            >
              <LogOut className="w-4 h-4 mr-2" /> Déconnexion
            </Button>
          </div>
        </aside>

        <main className="flex-1 max-w-6xl mx-auto p-4 lg:p-8 w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
